import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import AddChannelModal from "../components/modals/AddChannelModal";
import ChannelAddedModal from "../components/modals/ChannelAddelModal";
import ChannelPendingModal from "../components/modals/ChannelPendingModal";
import AddToSlackModal from "../components/modals/AddUserToSlack";
import AddRecordingFlowWrapper from "../components/modals/AddRecordingFlowWrapper";
import AddUserModal from "../components/modals/AddUserModal";
import SupportComposeModal from "../components/modals/SupportComposeModal";

const AssistantUiBridgeContext = createContext(null);

const getChannelsFromStorage = () =>
  JSON.parse(localStorage.getItem("organization") || "{}")?.channels?.L || [];
const getRecordingsFromStorage = () =>
  JSON.parse(localStorage.getItem("organization") || "{}")?.recordings?.L || [];

/**
 * Dashboard-level bridge for onboarding assistant tools:
 * open channel/recording/user/support modals, draft route updates, emit success events.
 */
export function AssistantUiBridgeProvider({ children, onAssistantEvent }) {
  const onEventRef = useRef(onAssistantEvent);
  onEventRef.current = onAssistantEvent;

  const emit = useCallback((type, detail = {}) => {
    if (typeof onEventRef.current === "function") {
      onEventRef.current({ type, ...detail });
    }
  }, []);

  const [channels, setChannelsState] = useState(getChannelsFromStorage);
  const [recordings, setRecordingsState] = useState(getRecordingsFromStorage);

  const [addChannelOpen, setAddChannelOpen] = useState(false);
  const [addChannelInitialType, setAddChannelInitialType] = useState(null);
  const [addChannelInitialValue, setAddChannelInitialValue] = useState("");
  const [addRecordingOpen, setAddRecordingOpen] = useState(false);

  const [openChannelAddedModal, setOpenChannelAddedModal] = useState(false);
  const [openChannelPendingModal, setOpenChannelPendingModal] = useState(false);
  const [addToSlackModal, setOpenAddToSlackModal] = useState(false);
  const [channelId, setChannelId] = useState(null);

  const [addUserOpen, setAddUserOpen] = useState(false);
  const [addUserLoader, setAddUserLoader] = useState(false);
  const [users, setUsers] = useState([]);

  const [supportComposeOpen, setSupportComposeOpen] = useState(false);
  const [supportComposePrefill, setSupportComposePrefill] = useState({
    subject: "",
    body: "",
  });

  const draftRouteUpdateRef = useRef(null);
  const getRouteDraftRef = useRef(null);

  const setChannels = useCallback((next) => {
    setChannelsState(next);
    const org = JSON.parse(localStorage.getItem("organization") || "null");
    if (org) {
      org.channels = { L: next };
      localStorage.setItem("organization", JSON.stringify(org));
    }
  }, []);

  const setRecordings = useCallback((next) => {
    setRecordingsState(next);
    const org = JSON.parse(localStorage.getItem("organization") || "null");
    if (org) {
      org.recordings = { L: next };
      localStorage.setItem("organization", JSON.stringify(org));
    }
  }, []);

  const openAddChannelModal = useCallback((initialType = null, options = {}) => {
    setAddChannelInitialType(initialType);
    setAddChannelInitialValue(options.initialValue || "");
    setAddChannelOpen(true);
  }, []);

  const openAddRecordingModal = useCallback(() => {
    setAddRecordingOpen(true);
  }, []);

  const openAddUserModal = useCallback(() => {
    setAddUserOpen(true);
  }, []);

  const openSupportComposeModal = useCallback((prefill = {}) => {
    setSupportComposePrefill({
      subject: prefill.subject || "",
      body: prefill.body || "",
    });
    setSupportComposeOpen(true);
  }, []);

  const registerRouteDraftHandler = useCallback((fn) => {
    draftRouteUpdateRef.current = typeof fn === "function" ? fn : null;
    return () => {
      if (draftRouteUpdateRef.current === fn) {
        draftRouteUpdateRef.current = null;
      }
    };
  }, []);

  const registerRouteDraftGetter = useCallback((fn) => {
    getRouteDraftRef.current = typeof fn === "function" ? fn : null;
    return () => {
      if (getRouteDraftRef.current === fn) {
        getRouteDraftRef.current = null;
      }
    };
  }, []);

  const draftRouteUpdate = useCallback((patch) => {
    if (!draftRouteUpdateRef.current) {
      throw new Error("No route editor is registered for drafts");
    }
    return draftRouteUpdateRef.current(patch);
  }, []);

  const getRouteDraft = useCallback(() => {
    if (typeof getRouteDraftRef.current !== "function") return null;
    try {
      return getRouteDraftRef.current();
    } catch {
      return null;
    }
  }, []);

  const callRecordingAddedAndClear = useCallback(
    (newRecId) => {
      emit("recording_added", { recordingId: newRecId });
      setRecordings(getRecordingsFromStorage());
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("twilanswer-org-assets-changed"));
      }
    },
    [emit, setRecordings]
  );

  const callChannelAddedAndClear = useCallback(
    (newChannelValue) => {
      emit("channel_added", { channelValue: newChannelValue });
      setChannels(getChannelsFromStorage());
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("twilanswer-org-assets-changed"));
      }
    },
    [emit, setChannels]
  );

  const notifyRouteSaved = useCallback(
    (detail = {}) => {
      emit("route_saved", detail);
    },
    [emit]
  );

  const notifyCallEnded = useCallback(
    (detail = {}) => {
      emit("call_ended", detail);
    },
    [emit]
  );

  const notifyCallDetailsOpened = useCallback(
    (detail = {}) => {
      emit("call_details_opened", detail);
    },
    [emit]
  );

  const notifyError = useCallback(
    (detail = {}) => {
      emit("error", detail);
    },
    [emit]
  );

  const notifySupportSent = useCallback(
    (detail = {}) => {
      emit("support_sent", detail);
    },
    [emit]
  );

  const value = useMemo(
    () => ({
      openAddChannelModal,
      openAddRecordingModal,
      openAddUserModal,
      openSupportComposeModal,
      draftRouteUpdate,
      getRouteDraft,
      registerRouteDraftHandler,
      registerRouteDraftGetter,
      notifyRouteSaved,
      notifyCallEnded,
      notifyCallDetailsOpened,
      notifyError,
      notifySupportSent,
      emit,
    }),
    [
      openAddChannelModal,
      openAddRecordingModal,
      openAddUserModal,
      openSupportComposeModal,
      draftRouteUpdate,
      getRouteDraft,
      registerRouteDraftHandler,
      registerRouteDraftGetter,
      notifyRouteSaved,
      notifyCallEnded,
      notifyCallDetailsOpened,
      notifyError,
      notifySupportSent,
      emit,
    ]
  );

  // Refresh channel/recording lists when opening modals
  const handleSetAddChannelOpen = useCallback((open) => {
    if (open) setChannels(getChannelsFromStorage());
    setAddChannelOpen(open);
  }, [setChannels]);

  const handleSetAddRecordingOpen = useCallback((open) => {
    if (open) setRecordings(getRecordingsFromStorage());
    setAddRecordingOpen(open);
  }, [setRecordings]);

  return (
    <AssistantUiBridgeContext.Provider value={value}>
      {children}
      <ChannelAddedModal
        setChannels={setChannels}
        open={openChannelAddedModal}
        setOpen={setOpenChannelAddedModal}
      />
      <ChannelPendingModal
        open={openChannelPendingModal}
        setOpen={setOpenChannelPendingModal}
      />
      <AddToSlackModal
        open={addToSlackModal}
        setOpen={setOpenAddToSlackModal}
        channel_id={channelId}
        setChannelId={setChannelId}
      />
      <AddChannelModal
        open={addChannelOpen}
        setOpen={handleSetAddChannelOpen}
        channels={channels}
        setChannels={setChannels}
        channelAddedModal={openChannelAddedModal}
        setOpenChannelAddedModal={setOpenChannelAddedModal}
        channelPendingModal={openChannelPendingModal}
        setOpenChannelPendingModal={setOpenChannelPendingModal}
        addToSlackModal={addToSlackModal}
        setOpenAddToSlackModal={setOpenAddToSlackModal}
        setChannelId={setChannelId}
        initialChannelType={addChannelInitialType}
        initialChannelValue={addChannelInitialValue}
        callChannelAddedAndClear={callChannelAddedAndClear}
      />
      <AddRecordingFlowWrapper
        open={addRecordingOpen}
        setOpen={handleSetAddRecordingOpen}
        setRecordings={setRecordings}
        recordings={recordings}
        callRecordingAddedAndClear={callRecordingAddedAndClear}
      />
      <AddUserModal
        open={addUserOpen}
        setOpen={setAddUserOpen}
        setOpenUserInvitedModal={() => {
          setAddUserOpen(false);
          emit("user_invited", {});
        }}
        users={users}
        setUsers={setUsers}
        planDetails={null}
        fetchUsers={() => {}}
        loader={addUserLoader}
        setLoader={setAddUserLoader}
      />
      <SupportComposeModal
        open={supportComposeOpen}
        setOpen={setSupportComposeOpen}
        initialSubject={supportComposePrefill.subject}
        initialBody={supportComposePrefill.body}
        onSent={(detail) => {
          notifySupportSent(detail);
        }}
      />
    </AssistantUiBridgeContext.Provider>
  );
}

export function useAssistantUiBridge() {
  return useContext(AssistantUiBridgeContext);
}
